import { useCallback, useEffect, useMemo, useState } from 'react';
import { ActionableNotification, InlineNotification } from '@carbon/react';
import { useAtomValue, useSetAtom } from 'jotai';
import classNames from 'classnames';
import { debounce, startCase } from 'lodash-es';
import type { SortingState } from '@tanstack/react-table';
import { OutputSection } from 'components/output-section/output-section';
import {
  DataTable,
  type DataTableColumnDef,
  type DataTableColumnMeta,
  type TableState,
} from 'components/data-table';
import {
  activeFilterCountAtom,
  cashflowsVisibleColumnsAtom,
  mapScopeAtom,
  serverFiltersAtom,
} from './atoms';
import { useEval3Page } from './context';
import {
  useCalcDetail,
  useCalcLifecycle,
  useCashflowsByMonth,
  usePartialTracts,
} from './hooks';
import { toWarehouseColumnFilters } from './utils';
import type {
  CashflowMonthRow,
  CashflowSort,
  WarehouseColumnFilter,
} from './types';
import styles from './cashflows-by-month.module.scss';

// ─────────────────────────────────────
// Column Configuration
// ─────────────────────────────────────
// The KNOWN column base — the BE's curated finance-team ordering
// (`Warehouse::QueryService::CASHFLOWS_FINANCE_TEAM`, camelCased 1:1) with hand-tuned headers +
// number formats. These always render (even before data, so the table is a permanent fixture)
// and every id is a valid warehouse sort column. The endpoint returns EVERY warehouse column per
// row, so the long tail beyond this set is appended DYNAMICALLY (see `buildCashflowColumns`) —
// matching the legacy table — each with a startCase header + a value-inferred type.

// Shared meta shorthands — the type drives DataTable's cell formatter, so no raw
// numbers ever render.
const TEXT: DataTableColumnMeta = { type: 'text' };
const CURRENCY_2: DataTableColumnMeta = {
  type: 'currency',
  format: { maxDecimalPlaces: 2 },
};
const DECIMAL_2: DataTableColumnMeta = {
  type: 'decimal',
  format: { maxDecimalPlaces: 2 },
};
const INTEGER: DataTableColumnMeta = { type: 'integer' };
/** Ownership interests carry warehouse precision (10 dp). */
const INTEREST_10: DataTableColumnMeta = {
  type: 'decimal',
  format: { maxDecimalPlaces: 10 },
};

/**
 * One plain (accessor + meta) column definition.
 *
 * @param {string} key - The camelCase row key (also the sort column id).
 * @param {string} header - The display header.
 * @param {DataTableColumnMeta} meta - The type/format meta.
 * @return {DataTableColumnDef<CashflowMonthRow>} The column definition.
 */
const col = (
  key: string,
  header: string,
  meta: DataTableColumnMeta
): DataTableColumnDef<CashflowMonthRow> => ({
  id: key,
  accessorKey: key,
  header,
  meta,
});

/** The table's columns — `CASHFLOWS_FINANCE_TEAM` order, camelCased. */
const CASHFLOW_COLUMNS: DataTableColumnDef<CashflowMonthRow>[] = [
  col('slotRef', 'Slot Ref', TEXT),
  col('date', 'Date', { type: 'date' }),
  col('wellName', 'Well Name', TEXT),
  {
    id: 'slotCategory',
    accessorKey: 'slotCategory',
    header: 'Slot Category',
    // No meta.type with a custom cell — the body's formatter would override it.
    // The warehouse stores lowercase categories; render the display casing (PDP).
    cell: ({ getValue }) => {
      const value = getValue() as string | undefined;
      return value?.toUpperCase() || '';
    },
  },
  col('wellHoleDirection', 'Hole Direction', TEXT),
  col('developmentAreaStateAbbreviation', 'State', TEXT),
  col('developmentAreaName', 'Development Area', TEXT),
  col('basin', 'Basin', TEXT),
  col('tier', 'Tier', TEXT),
  col('formationName', 'Formation', TEXT),
  col('slotVolumeSource', 'Volume Source', TEXT),
  col('netRevenueInterest', 'Net Revenue Interest', INTEREST_10),
  col('netWorkingInterest', 'Net Working Interest', INTEREST_10),
  col('oilWellheadPrice', 'Oil Price ($/bbl)', CURRENCY_2),
  col('gasWellheadPrice', 'Gas Price ($/MMBTU)', CURRENCY_2),
  col('oilRealizedPrice', 'Oil Realized Price ($/bbl)', CURRENCY_2),
  col('gasRealizedPrice', 'Gas Realized Price ($/MMBTU)', CURRENCY_2),
  col('nglRealizedPrice', 'NGL Realized Price ($/bbl)', CURRENCY_2),
  col('grossOil', 'Gross Oil (bbl)', INTEGER),
  col('grossGas', 'Gross Gas (mcf)', INTEGER),
  col('grossGasSold', 'Gross Gas Sold (mcf)', INTEGER),
  col('grossNgl', 'Gross NGL (bbl)', INTEGER),
  col('grossWater', 'Gross Water (bbl)', INTEGER),
  col('grossOilRevenue', 'Gross Oil Revenue ($)', CURRENCY_2),
  col('grossGasRevenue', 'Gross Gas Revenue ($)', CURRENCY_2),
  col('grossNglRevenue', 'Gross NGL Revenue ($)', CURRENCY_2),
  col('grossRevenue', 'Gross Revenue ($)', CURRENCY_2),
  col('grossOilTaxAmount', 'Gross Oil Tax Amount ($)', CURRENCY_2),
  col('grossGasTaxAmount', 'Gross Gas Tax Amount ($)', CURRENCY_2),
  col('grossNglTaxAmount', 'Gross NGL Tax Amount ($)', CURRENCY_2),
  col('adValoremTaxAmount', 'Ad Valorem Tax Amount ($)', CURRENCY_2),
  col('grossRevenueAfterTax', 'Gross Revenue After Tax ($)', CURRENCY_2),
  col('grossOilOpexCost', 'Gross Oil Opex ($)', CURRENCY_2),
  col('grossGasOpexCost', 'Gross Gas Opex ($)', CURRENCY_2),
  col('grossWaterOpexCost', 'Gross Water Opex ($)', CURRENCY_2),
  col('grossFixedCosts', 'Gross Fixed Costs ($)', CURRENCY_2),
  col('grossCosts', 'Gross Costs ($)', CURRENCY_2),
  col('afeDrillingCosts', 'AFE Drilling Costs ($)', CURRENCY_2),
  col('afeCompletionCosts', 'AFE Completion Costs ($)', CURRENCY_2),
  col('netOil', 'Net Oil (bbl)', DECIMAL_2),
  col('netGasSold', 'Net Gas Sold (mcf)', DECIMAL_2),
  col('netNgl', 'Net NGL (bbl)', DECIMAL_2),
  col('netWater', 'Net Water (bbl)', DECIMAL_2),
  col('netOilRevenue', 'Net Oil Revenue ($)', CURRENCY_2),
  col('netGasRevenue', 'Net Gas Revenue ($)', CURRENCY_2),
  col('netNglRevenue', 'Net NGL Revenue ($)', CURRENCY_2),
  col('netRevenue', 'Net Revenue ($)', CURRENCY_2),
  // The UMI signing bonus, on the month the lease is signed — its own line, because
  // it is not production revenue and its month may carry no volumes at all
  // (INS-10673).
  col('leaseBonus', 'Lease Bonus ($)', CURRENCY_2),
  col('netOilTaxAmount', 'Net Oil Tax Amount ($)', CURRENCY_2),
  col('netGasTaxAmount', 'Net Gas Tax Amount ($)', CURRENCY_2),
  col('netNglTaxAmount', 'Net NGL Tax Amount ($)', CURRENCY_2),
  col('netAdValoremTaxAmount', 'Net Ad Valorem Tax Amount ($)', CURRENCY_2),
  col('netRevenueAfterTax', 'Net Revenue After Tax ($)', CURRENCY_2),
  col('netOilOpexCost', 'Net Oil Opex ($)', CURRENCY_2),
  col('netGasOpexCost', 'Net Gas Opex ($)', CURRENCY_2),
  col('netWaterOpexCost', 'Net Water Opex ($)', CURRENCY_2),
  col('netFixedCosts', 'Net Fixed Costs ($)', CURRENCY_2),
  col('netCosts', 'Net Costs ($)', CURRENCY_2),
  col('netCashflow', 'Net Cashflow ($)', CURRENCY_2),
];

/** The curated (known) column ids — the dynamic tail is every OTHER warehouse key. */
const KNOWN_COLUMN_IDS = new Set(
  CASHFLOW_COLUMNS.map((column) => column.id).filter(
    (id): id is string => id !== undefined
  )
);

/** The warehouse key of the Asset Name column: a slot's first Tract, by name. */
const ASSET_NAME_KEY = 'assetName';

/** Matches an internal href-link row key (e.g. `wellHref`) — never shown as a column. */
const HREF_KEY = /href$/i;

/**
 * Whether a row key is an internal field, never shown as its own column — the row id
 * (`uniqueSlotId`) or any `*Href` link key.
 *
 * @param {string} key - The camelCase row key.
 * @return {boolean} True when the key is internal (excluded from the dynamic tail).
 */
const isInternalCashflowKey = (key: string): boolean =>
  key === 'uniqueSlotId' || HREF_KEY.test(key);

/**
 * The DataTable meta for a DYNAMIC (long-tail) column, inferred from a sample value so the cell
 * formatter still applies (no raw numbers): numbers render as 2-dp decimals, everything else
 * (strings / booleans / all-null) renders as text.
 *
 * @param {string | number | boolean | null} value - A sample cell value for the column.
 * @return {DataTableColumnMeta} The inferred meta.
 */
const inferCashflowMeta = (
  value: string | number | boolean | null
): DataTableColumnMeta => (typeof value === 'number' ? DECIMAL_2 : TEXT);

/**
 * Builds the table columns for the given rows: the curated {@link CASHFLOW_COLUMNS} base followed
 * by every ADDITIONAL warehouse column present in the data (the "long tail"), each with a
 * `startCase` header + a value-inferred type. Dynamic — matching the legacy table — so a new
 * warehouse column appears without an FE change. With no rows it returns just the base (the caller
 * falls back to the base until the next page lands).
 *
 * The Asset Name column names a slot's first Tract; one the package holds in part is followed by
 * its "(N of M Subtracts)", from `nameForAsset`.
 *
 * @param {CashflowMonthRow[]} rows - The loaded warehouse rows.
 * @param {Function} nameForAsset - An Asset as the tables name it (`usePartialTracts().nameForAsset`).
 * @return {DataTableColumnDef<CashflowMonthRow>[]} The base columns plus the dynamic tail.
 */
export const buildCashflowColumns = (
  rows: CashflowMonthRow[],
  nameForAsset: (name: string) => string
): DataTableColumnDef<CashflowMonthRow>[] => {
  const sample = rows[0];
  if (!sample) {
    return CASHFLOW_COLUMNS;
  }
  const tail = Object.keys(sample)
    .filter((key) => !KNOWN_COLUMN_IDS.has(key) && !isInternalCashflowKey(key))
    .map((key) => {
      // Infer the type from the first NON-NULL value for the key — row 0 may be null for a sparse
      // warehouse column; an all-null column falls back to text.
      const value = rows.find((row) => row[key] !== null)?.[key] ?? null;
      const column = col(key, startCase(key), inferCashflowMeta(value));
      if (key !== ASSET_NAME_KEY) {
        return column;
      }
      const labelled: DataTableColumnDef<CashflowMonthRow> = {
        ...column,
        cell: ({ getValue }) =>
          nameForAsset((getValue() as string | null) ?? ''),
      };
      return labelled;
    });
  return [...CASHFLOW_COLUMNS, ...tail];
};

/**
 * Maps TanStack's SortingState to the warehouse sort wire shape — the PRIMARY
 * entry only. The BE's keyset cursor is a single tuple comparison driven by the
 * first column's direction, so it can only page a uniform-direction ordering; a
 * mixed-direction multi-sort would corrupt pagination (see `withGrainTiebreakers`
 * in hooks.ts). Single-column sort also matches the master-data views' precedent
 * (`deriveSort`). Column ids are the camelCase row keys and pass through unchanged
 * — the BE underscores them (`Warehouse::QueryParams#to_snake_case`).
 *
 * @param {SortingState} sorting - The DataTable's post-change sorting state.
 * @return {CashflowSort[]} The primary server sort instruction (empty when unsorted).
 */
const toCashflowSort = (sorting: SortingState): CashflowSort[] => {
  const primary = sorting[0];
  if (!primary) {
    return [];
  }
  return [{ column: primary.id, dir: primary.desc ? 'desc' : 'asc' }];
};

/**
 * Stable row identity — the warehouse grain is one row per (unique_slot_id, date).
 * `uniqueSlotId` is the parquet's CANONICAL slot key: it's what the page filters
 * resolve to (`FilterSlotResolver` → `unique_slot_id IN`), what the BE dedupes on
 * (`COUNT(DISTINCT unique_slot_id)`), and it exists for well-less LTD slots too.
 * Deliberately NOT the legacy table's `date-slotRef-slotCategory` composite —
 * `slot_ref` is a display reference with no uniqueness guarantee across slots, so
 * that key can collide (the category term was a partial patch over it).
 *
 * @param {CashflowMonthRow} row - The warehouse row.
 * @return {string} The composite row id.
 */
const getRowId = (row: CashflowMonthRow): string =>
  `${row.uniqueSlotId}-${row.date}`;

// ─────────────────────────────────────
// Presentational table
// ─────────────────────────────────────

export interface CashflowsByMonthTableProps {
  /** The accumulated warehouse rows (all loaded pages, flattened). */
  rows: CashflowMonthRow[];
  /** Whether another cursor page exists (drives the infinite-scroll trigger). */
  hasNextPage: boolean;
  /**
   * ANY fetch in flight (first page / refetch / a follow-up cursor page). DataTable
   * derives the right affordance itself in infinite mode — a bottom spinner when
   * rows exist, the progress bar otherwise. (The section's DIM deliberately excludes
   * the cursor-page half before ORing it back in here.)
   */
  isLoading: boolean;
  /** Requests the next cursor page (virtualizer near-bottom; the TableState arg is unused). */
  onLoadMore: () => void;
  /** Server-side sort change (camelCase columns; BE default when empty). */
  onSortChange: (sort: CashflowSort[]) => void;
  /** Server-side global search change (raw input value; caller debounces). */
  onSearchChange: (search: string) => void;
  /** The table's columns (curated base + warehouse tail), built by the connected section. */
  columns: DataTableColumnDef<CashflowMonthRow>[];
  /** Reports the shown column ids in on-screen order (drives the export's Current View). */
  onVisibleColumnsChange: (columnIds: string[]) => void;
  /** Server-side per-column filter change — the translated warehouse predicates (INS-11272). */
  onColumnFiltersChange: (filters: WarehouseColumnFilter[]) => void;
}

/**
 * Presentational Cashflows-by-Month table — a `DataTable` over the columns the section
 * builds (curated base + warehouse long tail), in infinite-scroll mode (keyset cursors —
 * no totalCount, so no offset pager). Sorting + global search are SERVER-side (the
 * handlers emit the wire shapes). Pure: data in, callbacks out — no atoms / context / fetching
 * (the connected section handles those).
 *
 * The query error is deliberately NOT passed to DataTable: its internal error state
 * REPLACES the table body (kept rows included), which contradicts the section's
 * error-is-a-notification-over-the-fixture design — the section's
 * `ActionableNotification` is the single error surface.
 *
 * Column filters are SERVER-DRIVEN (INS-11272): the inline filter row's predicates are
 * translated to `{ column, op, value }` warehouse filters and ride the `filters` array
 * alongside the page-level dataset filters (the BE keeps the column-shaped entries and
 * resolves the attribute-shaped page filters separately, so the two AND). Export lives in
 * the central Content Export modal now (with a per-table column preset); this table reports
 * the columns it shows, in on-screen order, up via `onVisibleColumnsChange` so the modal's
 * "Current View" preset can export the layout on screen.
 *
 * @param {CashflowsByMonthTableProps} props - Rows + fetch state + the server-side change callbacks.
 * @return {JSX.Element} The infinite-scroll cashflows table.
 */
export const CashflowsByMonthTable = ({
  rows,
  hasNextPage,
  isLoading,
  onLoadMore,
  onSortChange,
  onSearchChange,
  columns,
  onVisibleColumnsChange,
  onColumnFiltersChange,
}: CashflowsByMonthTableProps) => {
  const handleSortChange = useCallback(
    (state: TableState) => onSortChange(toCashflowSort(state.sorting)),
    [onSortChange]
  );

  const handleSearchChange = useCallback(
    (state: TableState) => onSearchChange(state.globalFilter),
    [onSearchChange]
  );

  // Inline column filters (INS-11272): translate the DataTable's ColumnFiltersState
  // to warehouse `{ column, op, value }` predicates (this table owns the columns + meta)
  // and hand them up to the connected section, which threads them into the fetch.
  const handleFilterChange = useCallback(
    (state: TableState) =>
      onColumnFiltersChange(
        toWarehouseColumnFilters(state.columnFilters, columns)
      ),
    [onColumnFiltersChange, columns]
  );

  return (
    <DataTable<CashflowMonthRow>
      data={rows}
      columns={columns}
      getRowId={getRowId}
      isLoading={isLoading}
      scroll={{ mode: 'infinite', hasNextPage }}
      // Passed straight through (stable identity — an inline arrow would bust
      // DataTable's infiniteScroll memo every render); the zero-arg callback
      // ignores DataTable's TableState snapshot by design.
      onLoadMore={onLoadMore}
      onSortChange={handleSortChange}
      onGlobalSearchChange={handleSearchChange}
      onVisibleColumnsChange={onVisibleColumnsChange}
      onFilterChange={handleFilterChange}
      searchPlaceholder="Search cashflows..."
      config={{
        globalSearch: true,
        sorting: true,
        columnVisibility: true,
        columnResizing: true,
        columnReordering: true,
        columnPinning: true,
        // Server-driven: the inline filters ride the `filters` array as warehouse
        // `{ column, op, value }` predicates (INS-11272), AND-composed with the
        // page-level dataset filters by the controller.
        columnFilters: true,
      }}
      persistenceKey="eval3-new-cashflows-by-month"
    />
  );
};

// ─────────────────────────────────────
// Connected section
// ─────────────────────────────────────

/** How long a search keystroke waits before minting a new query key. */
const SEARCH_DEBOUNCE_MS = 300;

/**
 * Connected Cashflows-by-Month section — reads the page identity + the viewed
 * eval's calc detail (for the `Completed` fetch gate) + the dataset filters /
 * spatial scope, owns the server sort + search state, fetches the cursor-paginated
 * warehouse rows via `useCashflowsByMonth`, and drives the presentational table.
 * Mirrors `PackageOverviewSection`'s chrome exactly:
 *  - **Loading** — first page / refetch / calc run (`isRunning`): the table DIMS in
 *    place + the title spinner (`aria-busy` for a11y). A follow-up cursor page
 *    (`isFetchingNextPage`) does NOT dim — the bottom spinner alone signals it, so
 *    scroll-loading doesn't grey the rows being read.
 *  - **Error** — a notification + Retry ABOVE the table (never replaces it), shown
 *    only when NOT loading (a run/refetch in flight supersedes a stale error).
 *  - **Loaded** — the rows, with a "Filtered view" banner when a filter/scope is
 *    active. The table is the PERMANENT fixture (package mode / a mid-run calc
 *    render it empty — DataTable shows its own empty state).
 *
 * One DELIBERATE divergence from the mirrored siblings: the infinite query sets no
 * `placeholderData`, so a sort / search / filter / scope change (a new query
 * key) RESETS the accumulated pages — the table clears and reloads rather than
 * dimming the prior rows through the transition (correct for cursors: they're only
 * valid within their query's sort/filter set).
 *
 * @return {JSX.Element} The Cashflows by Month section.
 */
export function CashflowsByMonthSection() {
  const { calculationId } = useEval3Page();
  const { data: calcDetail } = useCalcDetail(calculationId);
  const filters = useAtomValue(serverFiltersAtom);
  const mapScope = useAtomValue(mapScopeAtom);
  const activeFilterCount = useAtomValue(activeFilterCountAtom);

  const [sort, setSort] = useState<CashflowSort[]>([]);
  const [search, setSearch] = useState('');
  // Inline per-column warehouse filters (INS-11272). Set from the table's translated
  // ColumnFiltersState; `setColumnFilters` is stable, so it's passed straight down.
  const [columnFilters, setColumnFilters] = useState<WarehouseColumnFilter[]>(
    []
  );
  // Debounce the search WRITE in the handler (not a useEffect watching input) so
  // each keystroke doesn't mint a new query key + refetch; `setSearch` is stable,
  // so the debounced wrapper is created once.
  const debouncedSetSearch = useMemo(
    () => debounce(setSearch, SEARCH_DEBOUNCE_MS),
    []
  );
  // Cancel a pending trailing write on unmount so it can't fire into a gone tree.
  useEffect(() => () => debouncedSetSearch.cancel(), [debouncedSetSearch]);

  const {
    data,
    isFetching,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    error,
    refetch,
  } = useCashflowsByMonth({
    calcId: calculationId,
    status: calcDetail?.status,
    sort,
    search,
    filters,
    columnFilters,
    mapScope,
  });

  // Flatten the accumulated cursor pages; memoized so the table's row-model memo
  // isn't busted by a fresh array identity every render.
  const rows = useMemo(
    () => data?.pages.flatMap((page) => page.rows) ?? [],
    [data]
  );

  const handleLoadMore = useCallback(() => {
    void fetchNextPage();
  }, [fetchNextPage]);

  // The unified loading umbrella: this section's OWN fetch OR the shared
  // calc-lifecycle wait (detail resolving / BE processing). `isFetchingNextPage`
  // is deliberately EXCLUDED — a scroll-triggered page must not dim the whole
  // table (the bottom spinner carries that signal via `isFetchingMore`).
  const { isRunning } = useCalcLifecycle(calculationId);
  const isLoading = (isFetching && !isFetchingNextPage) || isRunning;

  // The table's dynamic column set: the curated base + the warehouse long tail derived from the
  // rows. Keyed on the row KEY SET (invariant across infinite-scroll pages), NOT the `rows`
  // array, so appending a page doesn't needlessly rebuild the ~190-column list + churn
  // DataTable's column model. Empty rows (a filter/sort reset) fall back to the curated base.
  const columnKeySignature = rows[0] ? Object.keys(rows[0]).join('|') : '';
  const { nameForAsset } = usePartialTracts();
  const columns = useMemo(
    () =>
      rows[0] ? buildCashflowColumns(rows, nameForAsset) : CASHFLOW_COLUMNS,
    // eslint-disable-next-line react-hooks/exhaustive-deps -- rebuild only when the key set (or the labels) changes
    [columnKeySignature, nameForAsset]
  );

  // The table's `onVisibleColumnsChange` ids go to the shared atom as-is for the export's
  // "Current View" preset: every cashflow column is a data column. They stay raw camelCase: the
  // BE underscores them as it does filter/sort columns; lodash snakeCase would split digits
  // (loeScheduleYear3 → loe_schedule_year_3), matching no parquet column.
  const setCashflowsVisibleColumns = useSetAtom(cashflowsVisibleColumnsAtom);

  return (
    <OutputSection
      title="Cashflows by Month"
      isLoading={isLoading}
      defaultExpanded
    >
      {activeFilterCount > 0 && (
        <InlineNotification
          className={styles.banner}
          kind="info"
          lowContrast
          hideCloseButton
          title="Filtered view"
          subtitle="Showing results based on the filters applied in the left panel."
        />
      )}
      {/* Error is a NOTIFICATION over the fixture, never a replacement — Retry lives
              INSIDE it (ActionableNotification — InlineNotification can't host actions).
              Suppressed while loading: a run/refetch in flight supersedes a stale error. */}
      {error && !isLoading && (
        <ActionableNotification
          className={styles.banner}
          inline
          kind="error"
          // hasFocus off: its auto-focus scrolls the errored section into view (page jump)
          hasFocus={false}
          lowContrast
          hideCloseButton
          title="Couldn't load the cashflows"
          subtitle={error.message}
          actionButtonLabel="Retry"
          onActionButtonClick={() => refetch()}
        />
      )}
      {/* Permanent fixture — dims in place while loading (never while only a
              follow-up page is streaming in); aria-busy carries the a11y signal. The
              wrapper's FIXED height is what makes infinite scroll work (see the scss). */}
      <div
        className={classNames(styles.tableWrapper, {
          [styles.dimmed]: isLoading,
        })}
        aria-busy={isLoading}
      >
        <CashflowsByMonthTable
          rows={rows}
          hasNextPage={hasNextPage ?? false}
          // OR the cursor-page half back in for the TABLE (its bottom spinner);
          // the section's DIM above deliberately excludes it.
          isLoading={isLoading || isFetchingNextPage}
          onLoadMore={handleLoadMore}
          onSortChange={setSort}
          onSearchChange={debouncedSetSearch}
          columns={columns}
          onVisibleColumnsChange={setCashflowsVisibleColumns}
          onColumnFiltersChange={setColumnFilters}
        />
      </div>
    </OutputSection>
  );
}
